import React, { useState, useRef, useEffect } from 'react';
import { Shield, Lock, Eye, EyeOff, AlertTriangle, CheckCircle, Fingerprint, Smartphone } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { initializeVault, encryptData } from '../utils/vaultCrypto';
import { isBiometricAvailable, setupBiometricBackup, setupSimpleBiometricBackup } from '../utils/biometricBackup';

const VaultPINSetup = ({ onComplete, userName }) => {
  const [step, setStep] = useState('intro'); // intro, create, confirm, biometric
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '', '', '']);
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(null); // null = checking, true/false = result
  const [vaultData, setVaultData] = useState(null); // Store temporarily for biometric setup
  const inputRefs = useRef([]);
  const confirmInputRefs = useRef([]);

  // Check if this is likely an iOS/mobile device
  const isMobileDevice = () => {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  };

  useEffect(() => {
    // Check biometric support
    const checkBiometric = async () => {
      try {
        const available = await isBiometricAvailable();
        console.log('Biometric available:', available);
        setBiometricSupported(available);
      } catch (e) {
        console.log('Biometric check error:', e);
        // On mobile devices, still show the option - let user try
        setBiometricSupported(isMobileDevice());
      }
    };
    checkBiometric();
  }, []);

  useEffect(() => {
    // Small delay for iOS compatibility
    const timer = setTimeout(() => {
      if (step === 'create' && inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
      if (step === 'confirm' && confirmInputRefs.current[0]) {
        confirmInputRefs.current[0].focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [step]);

  const handlePinChange = (index, value, isConfirm = false) => {
    if (!/^\d*$/.test(value)) return; // Only digits
    
    const newPin = isConfirm ? [...confirmPin] : [...pin];
    newPin[index] = value.slice(-1); // Only last digit
    
    if (isConfirm) {
      setConfirmPin(newPin);
    } else {
      setPin(newPin);
    }
    
    setError('');
    
    // Auto-focus next input
    if (value && index < 5) {
      const refs = isConfirm ? confirmInputRefs : inputRefs;
      refs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e, isConfirm = false) => {
    if (e.key === 'Backspace') {
      const currentPin = isConfirm ? confirmPin : pin;
      if (!currentPin[index] && index > 0) {
        const refs = isConfirm ? confirmInputRefs : inputRefs;
        refs.current[index - 1]?.focus();
      }
    }
  };

  const handleCreatePIN = () => {
    const pinString = pin.join('');
    if (pinString.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }
    
    // Check for weak PINs
    const weakPins = ['000000', '111111', '222222', '333333', '444444', '555555', 
                      '666666', '777777', '888888', '999999', '123456', '654321'];
    if (weakPins.includes(pinString)) {
      setError('Please choose a stronger PIN');
      return;
    }
    
    setStep('confirm');
  };

  const handleConfirmPIN = async () => {
    const pinString = pin.join('');
    const confirmPinString = confirmPin.join('');
    
    if (pinString !== confirmPinString) {
      setError('PINs do not match. Please try again.');
      setConfirmPin(['', '', '', '', '', '']);
      confirmInputRefs.current[0]?.focus();
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Initialize vault with PIN
      const { salt, key } = await initializeVault(pinString);
      
      // Create a test encrypted value to verify PIN later
      const testValue = await encryptData('vault_test_' + Date.now(), key);
      
      // Store vault data temporarily for biometric setup
      setVaultData({ salt, key, testValue, pin: pinString });
      
      // ALWAYS show biometric option on mobile devices, even if check failed
      // The check can fail for various reasons but Face ID might still work
      if (biometricSupported === true || isMobileDevice()) {
        setStep('biometric');
      } else {
        // Desktop without biometric - complete immediately
        onComplete({ salt, key, testValue });
      }
    } catch (err) {
      setError('Failed to create vault. Please try again.');
      console.error('Vault creation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const [biometricError, setBiometricError] = useState('');
  const [biometricSuccess, setBiometricSuccess] = useState(false);

  const handleBiometricSetup = async (enable) => {
    setBiometricError('');
    setBiometricSuccess(false);
    
    if (enable && vaultData) {
      setLoading(true);
      try {
        // Generate a unique user ID for WebAuthn
        const userId = localStorage.getItem('sehatSetu_user') 
          ? JSON.parse(localStorage.getItem('sehatSetu_user')).id 
          : `user_${Date.now()}`;
        
        // Try WebAuthn first
        const result = await setupBiometricBackup(userId, vaultData.salt, vaultData.pin);
        
        if (result.success) {
          setBiometricSuccess(true);
          // Wait a moment to show success before completing
          setTimeout(() => {
            onComplete({
              salt: vaultData.salt,
              key: vaultData.key,
              testValue: vaultData.testValue
            });
          }, 1500);
          return;
        } else {
          // Show error but still allow completion
          setBiometricError(result.error || 'Setup failed. You can try again in Settings.');
        }
      } catch (err) {
        console.error('Biometric setup error:', err);
        setBiometricError('Face ID setup failed. You can try again later in Settings.');
      } finally {
        setLoading(false);
      }
    }
    
    // Complete vault setup if not enabling or if setup failed (user can still proceed)
    if (!enable || biometricError) {
      if (vaultData) {
        onComplete({
          salt: vaultData.salt,
          key: vaultData.key,
          testValue: vaultData.testValue
        });
      }
    }
  };

  // Single hidden input approach for better iOS compatibility
  const PinInputGroup = ({ pinState, refs, isConfirm = false }) => {
    const hiddenInputRef = useRef(null);
    const pinString = pinState.join('');
    
    const handleHiddenInput = (e) => {
      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
      const newPin = [...pinState];
      for (let i = 0; i < 6; i++) {
        newPin[i] = value[i] || '';
      }
      if (isConfirm) {
        setConfirmPin(newPin);
      } else {
        setPin(newPin);
      }
      setError('');
    };
    
    const handleHiddenKeyDown = (e) => {
      if (e.key === 'Backspace' && pinString.length > 0) {
        const newPin = [...pinState];
        newPin[pinString.length - 1] = '';
        if (isConfirm) {
          setConfirmPin(newPin);
        } else {
          setPin(newPin);
        }
      }
    };
    
    const focusInput = () => {
      hiddenInputRef.current?.focus();
    };
    
    // Auto-focus on mount
    useEffect(() => {
      if ((step === 'create' && !isConfirm) || (step === 'confirm' && isConfirm)) {
        setTimeout(() => focusInput(), 100);
      }
    }, [step, isConfirm]);
    
    return (
      <div className="relative">
        {/* Hidden input for actual typing */}
        <input
          ref={hiddenInputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          value={pinString}
          onChange={handleHiddenInput}
          onKeyDown={handleHiddenKeyDown}
          className="absolute opacity-0 w-full h-full cursor-pointer"
          maxLength={6}
          data-testid={`pin-hidden-input-${isConfirm ? 'confirm' : 'create'}`}
        />
        {/* Visual display boxes */}
        <div 
          className="flex justify-center gap-3 cursor-pointer"
          onClick={focusInput}
          onTouchStart={focusInput}
        >
          {pinState.map((digit, index) => (
            <div
              key={index}
              className={`w-12 h-14 flex items-center justify-center text-2xl font-bold border-2 rounded-xl 
                         transition-all select-none
                         ${index === pinString.length ? 'border-[#0B7EA3] ring-2 ring-[#0B7EA3]/20' : 'border-slate-200'}
                         ${digit ? 'bg-slate-50' : 'bg-white'}`}
              data-testid={`pin-display-${isConfirm ? 'confirm-' : ''}${index}`}
            >
              {digit ? (showPin ? digit : '•') : ''}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (step === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0D2340] to-[#0B7EA3] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#0B7EA3]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-[#0B7EA3]" />
            </div>
            <CardTitle className="text-2xl">Secure Your Health Vault</CardTitle>
            <CardDescription className="text-base mt-2">
              Hi {userName}, let's protect your health data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <h4 className="font-semibold text-green-800 flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Your Data, Your Control
              </h4>
              <p className="text-sm text-green-700 mt-2">
                Your health data is encrypted on YOUR device with YOUR PIN. 
                We never see your unencrypted data - only you can access it.
              </p>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Important
              </h4>
              <p className="text-sm text-amber-700 mt-2">
                If you forget your PIN, we cannot recover your data.
                Choose a PIN you'll remember!
              </p>
            </div>
            
            <Button 
              onClick={() => setStep('create')}
              className="w-full h-12 text-lg bg-[#0D2340] hover:bg-[#0D2340]/90"
              data-testid="create-pin-btn"
            >
              <Lock className="h-5 w-5 mr-2" />
              Create My Vault PIN
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'create') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0D2340] to-[#0B7EA3] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#0B7EA3]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-[#0B7EA3]" />
            </div>
            <CardTitle className="text-2xl">Create Your 6-Digit PIN</CardTitle>
            <CardDescription>This PIN will encrypt all your health data</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <PinInputGroup pinState={pin} refs={inputRefs} />
            
            <button
              onClick={() => setShowPin(!showPin)}
              className="flex items-center justify-center gap-2 text-sm text-slate-600 mx-auto"
            >
              {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {showPin ? 'Hide PIN' : 'Show PIN'}
            </button>
            
            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}
            
            <Button 
              onClick={handleCreatePIN}
              className="w-full h-12 text-lg bg-[#0D2340] hover:bg-[#0D2340]/90"
              disabled={pin.join('').length !== 6}
              data-testid="next-btn"
            >
              Next
            </Button>
            
            <p className="text-xs text-slate-500 text-center">
              Avoid simple PINs like 123456 or 000000
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'confirm') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0D2340] to-[#0B7EA3] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#0B7EA3]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-[#0B7EA3]" />
            </div>
            <CardTitle className="text-2xl">Confirm Your PIN</CardTitle>
            <CardDescription>Enter the same PIN again to confirm</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <PinInputGroup pinState={confirmPin} refs={confirmInputRefs} isConfirm />
            
            <button
              onClick={() => setShowPin(!showPin)}
              className="flex items-center justify-center gap-2 text-sm text-slate-600 mx-auto"
            >
              {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {showPin ? 'Hide PIN' : 'Show PIN'}
            </button>
            
            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}
            
            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={() => {
                  setStep('create');
                  setConfirmPin(['', '', '', '', '', '']);
                  setError('');
                }}
                className="flex-1 h-12"
              >
                Back
              </Button>
              <Button 
                onClick={handleConfirmPIN}
                className="flex-1 h-12 bg-[#0D2340] hover:bg-[#0D2340]/90"
                disabled={confirmPin.join('').length !== 6 || loading}
                data-testid="confirm-pin-btn"
              >
                {loading ? 'Creating...' : 'Next'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 4: Biometric backup option
  if (step === 'biometric') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0D2340] to-[#0B7EA3] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#0B7EA3]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Fingerprint className="h-8 w-8 text-[#0B7EA3]" />
            </div>
            <CardTitle className="text-2xl">Enable Face ID / Touch ID?</CardTitle>
            <CardDescription>Add a backup to recover your vault if you forget your PIN</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-[#0B7EA3]/10 border border-[#0B7EA3]/20 rounded-xl p-4">
              <h4 className="font-semibold text-[#0D2340] flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                How it works
              </h4>
              <p className="text-sm text-slate-600 mt-2">
                Your PIN will be securely stored on this device. If you forget your PIN, 
                you can use Face ID or Touch ID to unlock your vault.
              </p>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Note
              </h4>
              <p className="text-sm text-amber-700 mt-2">
                This backup only works on this device. If you switch devices, 
                you'll need your PIN.
              </p>
            </div>
            
            {biometricSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <h4 className="font-semibold text-green-800 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Face ID / Touch ID Enabled!
                </h4>
                <p className="text-sm text-green-700 mt-2">
                  You can now use Face ID to unlock your vault.
                </p>
              </div>
            )}
            
            {biometricError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <h4 className="font-semibold text-red-800 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Setup Issue
                </h4>
                <p className="text-sm text-red-700 mt-2">
                  {biometricError}
                </p>
              </div>
            )}
            
            <div className="space-y-3">
              {!biometricSuccess && (
                <Button 
                  onClick={() => handleBiometricSetup(true)}
                  className="w-full h-12 bg-[#0D2340] hover:bg-[#0D2340]/90"
                  disabled={loading}
                  data-testid="enable-biometric-btn"
                >
                  <Fingerprint className="h-5 w-5 mr-2" />
                  {loading ? 'Setting up Face ID...' : 'Enable Face ID / Touch ID'}
                </Button>
              )}
              
              <Button 
                variant={biometricSuccess ? "default" : "ghost"}
                onClick={() => handleBiometricSetup(false)}
                className={biometricSuccess ? "w-full h-12 bg-[#0B7EA3] hover:bg-[#0B7EA3]/90" : "w-full h-12 text-slate-600"}
                disabled={loading}
                data-testid="skip-biometric-btn"
              >
                {biometricSuccess ? 'Continue to Dashboard' : 'Skip for now'}
              </Button>
            </div>
            
            <p className="text-xs text-slate-500 text-center">
              You can enable this later in Settings → Vault PIN
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default VaultPINSetup;
